import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Heart, ArrowLeft, Mail, CheckCircle, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import apiClient from '../services/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resetLink, setResetLink] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResetLink('');
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      setSuccess(true);
      setEmailSent(response.data.email_sent);
      
      // If email wasn't sent but we have a reset link (development mode)
      if (!response.data.email_sent && response.data.reset_link) {
        setResetLink(response.data.reset_link);
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      const errorDetail = err.response?.data?.detail;
      if (errorDetail) {
        setError(errorDetail);
      } else if (err.message === 'Network Error') {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetLinkClick = () => {
    if (resetLink) {
      // Extract the token from the reset link
      const url = new URL(resetLink);
      const token = url.searchParams.get('token');
      if (token) {
        navigate(`/reset-password?token=${token}`);
      }
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl p-8 shadow-xl text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {emailSent ? 'Check Your Email' : 'Reset Link Generated'}
            </h2>
            
            {emailSent ? (
              <>
                <p className="text-muted-foreground mb-6">
                  We've sent a password reset link to <strong className="text-foreground">{email}</strong>
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
              </>
            ) : resetLink ? (
              <>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-left">
                      <p className="text-sm text-amber-500 font-medium">Email service not configured</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Click the button below to reset your password directly.
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleResetLinkClick}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 mb-4"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Reset Password Now
                </Button>
              </>
            ) : (
              <p className="text-muted-foreground mb-6">
                If an account exists with <strong className="text-foreground">{email}</strong>, you will receive a reset link.
              </p>
            )}
            
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setSuccess(false);
                  setResetLink('');
                  setEmailSent(false);
                }}
                variant="outline"
                className="w-full"
              >
                Try Another Email
              </Button>
              <Link to="/login">
                <Button variant="ghost" className="w-full">
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
          </Link>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-500 bg-clip-text text-transparent">
            FundTheHope
          </h1>
        </div>

        {/* Form */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500/20 to-teal-600/20 rounded-full mx-auto mb-6">
            <Mail className="w-8 h-8 text-emerald-400" />
          </div>
          
          <h2 className="text-2xl font-bold text-center text-foreground mb-2">
            Forgot Password?
          </h2>
          <p className="text-muted-foreground text-center mb-6">
            No worries! Enter your email and we'll send you a reset link.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 border-2 border-emerald-500/40"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-500 text-sm text-center">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full py-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              disabled={loading || !email}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-emerald-400 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
